import type { InsurancePlan } from 'src/services/interfaces/insurance';
import { resolveIdCardNumbers } from 'src/config/cardDefaults';

interface CignaCardProps {
    side?: 'front' | 'back';
    plan?: InsurancePlan;
    memberId?: string;
    groupId?: string;
    groupName?: string;
    rxGroupId?: string;
    rxCosts?: string;
    subscriberName?: string;
    memberName?: string;
    memberCode?: string;
}

type CoverageDetails = Record<string, number | string | undefined>;

const defaultCignaGroup = 'HC002';
const defaultGroupName = 'IHA Services';

const getCoverage = (plan?: InsurancePlan): CoverageDetails =>
    ((plan?.details as Record<string, unknown> | undefined)?.coverage || {}) as CoverageDetails;

const formatMoney = (value: number | string | undefined) => {
    if (value === undefined || value === null || value === '') return '—';

    const raw = String(value).trim();
    if (!raw) return '—';
    if (raw.startsWith('$')) return raw;

    const numeric = Number(raw.replace(/[$,]/g, ''));
    if (!Number.isNaN(numeric)) {
        return `$${numeric.toLocaleString('en-US')}`;
    }

    return raw;
};

const formatBenefitPair = (
    coverage: CoverageDetails,
    individualKey: string,
    familyKey: string
) => {
    const individual = formatMoney(coverage[individualKey]);
    const family = formatMoney(coverage[familyKey]);

    if (individual === '—' && family === '—') return '—';
    if (family === '—') return individual;
    if (individual === '—') return family;

    return `${individual}/${family}`;
};

const formatCardValue = (
    value: number | string | undefined,
    fallback = '$00/00%'
) => {
    if (value === undefined || value === null || value === '') return fallback;
    return String(value);
};

const SectionBar = ({ children }: { children: string }) => (
    <div className="bg-[#1c1c1c] px-3 py-1.5 text-center text-sm font-bold text-white">
        <span data-id-card-section-bar-label>{children}</span>
    </div>
);

export const CignaCard = ({
    side = 'front',
    plan,
    memberId,
    groupId,
    groupName,
    rxGroupId,
    rxCosts,
    subscriberName,
    memberName,
    memberCode,
}: CignaCardProps) => {
    const coverage = getCoverage(plan);
    const rxCostsFromApi = coverage['Rx Costs'] as string | undefined;
    const {
        groupNumber: resolvedGroupNumber,
        rxGroupNumber: resolvedRxGroupNumber,
    } = resolveIdCardNumbers({
        plan,
        groupId,
        rxGroupId,
    });

    const displayMemberId = memberId?.trim() || '{1234567}';
    const displayGroupNumber = resolvedGroupNumber || defaultCignaGroup;
    const displayRxGroupNumber =
        resolvedRxGroupNumber || displayGroupNumber || defaultCignaGroup;
    const displayGroupName = groupName?.trim() || defaultGroupName;
    const displayRxCosts = rxCosts?.trim() || rxCostsFromApi || '—';
    const displaySubscriberName = subscriberName?.trim() || '{Primary Applicant}';
    const displayMemberName = memberName?.trim() || displaySubscriberName;
    const displayMemberCode = memberCode?.trim() || '01';

    const inNetworkDeductible = formatBenefitPair(
        coverage,
        'Individual Deductible (In-Network)',
        'Family Deductible (In-Network)'
    );
    const inNetworkOutOfPocket = formatBenefitPair(
        coverage,
        'Individual Max Out-of-Pocket (In-Network)',
        'Family Max Out-of-Pocket (In-Network)'
    );
    const outOfNetworkDeductible = formatBenefitPair(
        coverage,
        'Individual Deductible (Out-of-Network)',
        'Family Deductible (Out-of-Network)'
    );
    const outOfNetworkOutOfPocket = formatBenefitPair(
        coverage,
        'Individual Max Out-of-Pocket (Out-of-Network)',
        'Family Max Out-of-Pocket (Out-of-Network)'
    );

    if (side === 'back') {
        return (
            <div className="relative aspect-[672/411] max-w-[672px] origin-center scale-60 rounded-2xl border-2 border-dashed border-[#cfcfcf] bg-white px-6 py-5 font-sans text-black md:scale-100">
                <img
                    src="/images/securus-benefits.png"
                    alt="Securus Benefits"
                    className="absolute right-6 top-5 h-11 w-auto"
                />

                <div className="flex h-full flex-col">
                    <div className="pr-36 text-[13px] font-normal leading-[1.2]">
                        <div>
                            <div className="mb-0.5 text-[15px] font-bold text-[#416ab2]">Providers:</div>
                            <div>
                                <span className="font-bold">For prescription claims/inquiries call:</span> (888) 589-3340
                            </div>
                            <div className="font-bold">For eligibility, coverage, medical pre-cert, and claim status:</div>
                            <div>Payer Name (Availity): Yuzu Health</div>
                            <div>Go to portal.securusbenefits.com/providers or call&nbsp; (877) 420-0785</div>
                        </div>

                        <div className="mt-4">
                            <div className="mb-0.5 text-[15px] font-bold text-[#416ab2]">Members:</div>
                            <div>Member portal: portal.securusbenefits.com</div>
                            <div>For plan, care, billing, or claims questions, call: (877) 420-0785</div>
                            <div>For prescription questions, call: (888) 589-3340</div>
                        </div>
                    </div>

                    <div className="mt-4 text-[13px] font-normal leading-[1.2]">
                        <div className="mb-0.5 text-[15px] font-bold text-[#416ab2]">Submit claims to:</div>
                        <div className="grid grid-cols-[1fr_1.1fr] gap-14">
                            <div>
                                <div>Medical: Cigna Health</div>
                                <div>Payer ID: 62308</div>
                                <div>PO Box 188061</div>
                                <div>Chattanooga, TN, 37422</div>
                            </div>
                            <div>
                                <div>Pharmacy: DisclosedRx</div>
                                <div>Rx BIN: 021601</div>
                                <div>Rx PCN: DRX</div>
                                <div>Rx Group #: {displayRxGroupNumber}</div>
                            </div>
                        </div>
                    </div>

                    <p className="mt-auto px-3 text-center text-[9px] font-semibold leading-[1.18]">
                        This card is not a guarantee of coverage. Coverage may be subject to limitations and conditions. Pre-certification is
                        required for some services. Failure to obtain pre-certification may result in a reduction of benefits. Member may be
                        eligible for financial assistance. Benefits are not insured by Cigna Healthcare or affiliates.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="relative aspect-[672/411] max-w-[672px] origin-center scale-60 rounded-2xl border-2 border-dashed border-[#cfcfcf] bg-white px-7 py-5 font-sans text-black md:scale-100">
            <div className="flex items-start justify-between border-b border-[#d5d5d5] pb-4">
                <div className="text-[16px] font-normal leading-[1.45]">
                    <h2 className="mb-1 text-xl font-normal">
                        <span className="font-bold">Member ID:</span> {displayMemberId}
                    </h2>
                    <div>
                        <span>Subscriber: </span>
                        <span>(01) {displaySubscriberName}</span>
                    </div>
                    <div>
                        <span>Member: </span>
                        <span>({displayMemberCode}) {displayMemberName}</span>
                    </div>
                    <div>Group #: {displayGroupNumber}</div>
                    <div>Group Name: {displayGroupName}</div>
                </div>

                <div className="w-[190px] pt-1 text-center">
                    <div className="mb-1 text-sm font-bold">Network Provided by:</div>
                    <img src="/images/cigna.png" alt="Cigna Healthcare" className="mx-auto h-14 w-[105px] object-contain" />
                    <div className="mt-1 text-[11px] leading-tight text-[#35b86a]">
                        <div className="font-bold">Shared Administration</div>
                        <div className="font-bold">PPO</div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-7 border-b border-[#d5d5d5] pb-2 pt-4 text-[16px] font-normal leading-[1.35]">
                <div>
                    <h3 className="mb-1 text-lg font-bold">In-Network</h3>
                    <div className="grid grid-cols-[1fr_auto] gap-x-4">
                        <span>Deductible</span>
                        <span>{inNetworkDeductible}</span>
                        <span>Max Out-of-Pocket</span>
                        <span>{inNetworkOutOfPocket}</span>
                    </div>
                </div>
                <div>
                    <h3 className="mb-1 text-lg font-bold">Out-Of-Network</h3>
                    <div className="grid grid-cols-[1fr_auto] gap-x-4">
                        <span>Deductible</span>
                        <span>{outOfNetworkDeductible}</span>
                        <span>Max Out-of-Pocket</span>
                        <span>{outOfNetworkOutOfPocket}</span>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-8 pt-5 text-[15px] font-normal leading-[1.45]">
                <div>
                    <SectionBar>Have a prescription question?</SectionBar>
                    <div className="mt-2 grid grid-cols-[auto_1fr] gap-x-6">
                        <span className="font-bold">Call PBM</span>
                        <span>(888) 589-3340</span>
                        <span className="font-bold">Rx Costs</span>
                        <span>{displayRxCosts}</span>
                    </div>
                    <img src="/images/disclosedrx.png" alt="DisclosedRx" className="mt-4 h-10 w-auto" />
                </div>

                <div>
                    <SectionBar>Cost Sharing (Price You Pay)</SectionBar>
                    <div className="mt-2 grid grid-cols-[auto_1fr] gap-x-6">
                        <span className="font-bold">Primary Care</span>
                        <span>{formatCardValue(coverage['Member Card - Primary Care'])}</span>
                        <span className="font-bold">Emergency</span>
                        <span>{formatCardValue(coverage['Member Card - Emergency'])}</span>
                        <span className="font-bold">Specialist</span>
                        <span>{formatCardValue(coverage['Member Card - Specialist'])}</span>
                        <span className="font-bold">Urgent Care</span>
                        <span>{formatCardValue(coverage['Member Card - Urgent Care'])}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};
